import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import type { DashboardResponse } from "../../services/dashboard-api";

const STATUS_LABEL: Record<string, string> = {
  not_started: "Not started",
  in_progress: "In progress",
  submitted: "Submitted",
  pending_grading: "Pending grading",
  completed: "Completed",
};

/** Recharts renders to SVG with no inherent text alternative, so each chart gets a `sr-only`
 * plain-text summary of the same data alongside it — the accessibility pass this session added
 * (T107), not present when the charts were first built. */
function summarize(data: { label: string }[], valueKey: "count" | "avg", suffix = ""): string {
  return data.map((d) => `${d.label}: ${(d as Record<string, number | string>)[valueKey]}${suffix}`).join(", ");
}

/** FR-017/FR-033: three standardized charts, driven entirely by props — no fetching of its own,
 * so the same component serves both the Manager's dashboard and the CO Member's per-company
 * view (T091). */
export function DashboardCharts({ charts }: { charts: DashboardResponse["charts"] }) {
  const completionData = charts.completionBreakdown.map((c) => ({
    label: STATUS_LABEL[c.status] ?? c.status,
    count: c.count,
  }));
  const distributionData = charts.scoreDistribution.map((d) => ({ label: d.bucket, count: d.count }));
  const departmentData = charts.avgScoreByDepartment.map((d) => ({
    label: d.department,
    avg: d.averageScorePercent,
  }));

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
      <div className="rounded border border-surface-alt p-4">
        <h3 className="mb-2 font-sans text-sm text-ink">Completion Status</h3>
        {completionData.length === 0 ? (
          <p className="text-sm text-body">No data yet.</p>
        ) : (
          <>
            <p className="sr-only">{summarize(completionData, "count")}</p>
            <ResponsiveContainer width="100%" height={200} aria-hidden="true">
              <BarChart data={completionData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#1A62FF" />
              </BarChart>
            </ResponsiveContainer>
          </>
        )}
      </div>

      <div className="rounded border border-surface-alt p-4">
        <h3 className="mb-2 font-sans text-sm text-ink">Score Distribution</h3>
        {distributionData.length === 0 ? (
          <p className="text-sm text-body">No finalized scores yet.</p>
        ) : (
          <>
            <p className="sr-only">{summarize(distributionData, "count")}</p>
            <ResponsiveContainer width="100%" height={200} aria-hidden="true">
              <BarChart data={distributionData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#1A62FF" />
              </BarChart>
            </ResponsiveContainer>
          </>
        )}
      </div>

      <div className="rounded border border-surface-alt p-4">
        <h3 className="mb-2 font-sans text-sm text-ink">Average Score by Department</h3>
        {departmentData.length === 0 ? (
          <p className="text-sm text-body">No finalized scores yet.</p>
        ) : (
          <>
            <p className="sr-only">{summarize(departmentData, "avg", "%")}</p>
            <ResponsiveContainer width="100%" height={200} aria-hidden="true">
              <BarChart data={departmentData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 100]} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="avg" fill="#1A62FF" />
              </BarChart>
            </ResponsiveContainer>
          </>
        )}
      </div>
    </div>
  );
}
